import { ref } from "vue";

import { REGEXP_URL } from "shared/constants";

import { urlVerificationPending } from "../shared/helpers";
import { type UrlData, UrlLocalStatus } from "../shared/types";

import useUrlsApiVerification from "./useUrlsApiVerification";
import {
  CannotBeAddedReason,
  UrlStatus,
} from "./useUrlVerificationNotificationsSubscription";

export default function useUrlsVerification() {
  const verificationInProgress = ref<boolean>(false);

  const { cancelUrlsWithApiVerification, verifyUrlsWithApi } =
    useUrlsApiVerification();

  async function performVerification(
    urlsData: UrlData[],
    { preVerifyLocally = true } = {}
  ) {
    verificationInProgress.value = true;

    if (preVerifyLocally) {
      preVerifyUrlsLocally(urlsData);
    }

    // check with API those that weren't conclusively verified locally
    const stillPendingUrls = urlsData.filter(urlVerificationPending);

    if (stillPendingUrls.length > 0) {
      try {
        await verifyUrlsWithApi(stillPendingUrls);
      } catch (error) {
        verificationInProgress.value = false;
        throw error;
      }
    }

    verificationInProgress.value = false;
  }

  function preVerifyUrlsLocally(urlsData: UrlData[]) {
    const urlsSoFar = new Set<string>();

    urlsData.forEach((urlData: UrlData) => {
      // modifying the original array to simplify the code (avoid passing
      // callbacks along) as we want to instantly see each url update in the UI

      /* eslint-disable no-param-reassign */
      if (!REGEXP_URL.test(urlData.url)) {
        urlData.status = UrlStatus.cannotBeAdded;
        urlData.reason = CannotBeAddedReason.invalidUrl;
      } else if (urlsSoFar.has(urlData.url)) {
        urlData.status = UrlStatus.cannotBeAdded;
        urlData.reason = CannotBeAddedReason.duplicatedUrl;
      }
      /* eslint-enable no-param-reassign */

      urlsSoFar.add(urlData.url);
    });
  }

  function cancelVerification() {
    cancelUrlsWithApiVerification();
    verificationInProgress.value = false;
  }

  function initializeUrlData(url: string): UrlData {
    return {
      status: UrlLocalStatus.pending,
      url,
    };
  }

  return {
    cancelVerification,
    initializeUrlData,
    performVerification,
    preVerifyUrlsLocally,
    verificationInProgress,
  };
}
